import { useEffect, useMemo, useState } from 'react';

import DistributionSelect from 'entities/Charts/components/DistributionSelect';
import {
  BAR_CHART_HEIGHT,
  BAR_CHART_WIDTH,
  GENERAL_CONCEPTUAL_UNDERSTANDING,
  NO_EVIDENCE_OF_STRATEGY,
  SIGNIFICANT_ERRORS_IN_WORK,
  WORK_IS_COMPLETE_AND_CORRECT
} from 'entities/Charts/constants';
import {
  ChartFilterProps,
  useOverallResponseChartData
} from 'entities/Charts/sdk';

import BarChart from 'components/BarChart';

import { notifyError } from 'utils/notifications';

const OverallResponseBarChart = ({
  filters
}: {
  filters: ChartFilterProps;
}) => {
  const [distributionOption, setDistributionOption] = useState<string>('grade');

  const { data, isLoading } = useOverallResponseChartData({
    option: distributionOption,
    filters: filters
  });

  const flatData = useMemo(() => {
    if (!data) return [];
    const { results } = data;
    return results.map((item) => ({
      group_field_type: item.group_field_type,
      group_field_value: item.group_field_value,
      ...item.group_results
    }));
  }, [data]);

  const messageForUser = useMemo(() => {
    if (data?.is_suppressed) {
      return 'You cannot view data for demographic subgroups smaller than 10 students!';
    }
    return '';
  }, [data]);

  useEffect(() => {
    if (messageForUser === 'You cannot view data for demographic subgroups smaller than 10 students!') {
      notifyError({ error: messageForUser });
    }
  }, [messageForUser]);

  return (
    <>
      {isLoading && (
        <BarChart
          loading
          series={[]}
          height={BAR_CHART_HEIGHT}
          width={BAR_CHART_WIDTH}
        />
      )}

      {!isLoading && (
        <BarChart
          dataset={flatData as readonly Record<string, any>[]}
          height={BAR_CHART_HEIGHT}
          width={BAR_CHART_WIDTH}
          xAxis={[{ dataKey: 'group_field_value' }]}
          localeText={{ noData: 'No data for selected filters' }}
          series={[
            {
              dataKey: GENERAL_CONCEPTUAL_UNDERSTANDING,
              label: GENERAL_CONCEPTUAL_UNDERSTANDING
            },
            {
              dataKey: WORK_IS_COMPLETE_AND_CORRECT,
              label: WORK_IS_COMPLETE_AND_CORRECT
            },
            {
              dataKey: NO_EVIDENCE_OF_STRATEGY,
              label: NO_EVIDENCE_OF_STRATEGY
            },
            {
              dataKey: SIGNIFICANT_ERRORS_IN_WORK,
              label: SIGNIFICANT_ERRORS_IN_WORK
            }
          ]}
          margin={{ top: 10, bottom: 30, left: 40, right: 10 }}
        />
      )}
      <DistributionSelect
        distributionOption={distributionOption}
        setDistributionOption={setDistributionOption}
      />
    </>
  );
};

export default OverallResponseBarChart;
